// Exports an assessment as a FlexCOA Assessment format CSV: the same layout
// as the Item Bank's template download, so a downloaded file can be imported
// back through the CSV importer (assessment_builder/views/import_views.py,
// _process_flexcoa_assessment_csv). -99 marks missing values, as in the import.

const MISSING = '-99';

// Question.type -> ITEM_TYPE. Types the importer has no code for are written
// as-is; it falls back to multiple_choice (with options) or text (without).
const ITEM_TYPE_BY_QUESTION_TYPE = {
  instruction: 'INST',
  ordinal: 'ORD',
  likert: 'ORD',
  rating: 'ORD',
  nrs: 'NRS',
  text: 'OpenText',
  nominal: 'Nominal',
  boolean: 'Y/N',
  yes_no: 'Y/N',
  vas: 'VAS',
  numeric: 'NUM',
};

// The importer drops options for these types, so don't export them either.
const TYPES_WITHOUT_OPTIONS = new Set(['instruction', 'text', 'vas']);

const toItemType = (type) => ITEM_TYPE_BY_QUESTION_TYPE[type] || type || '';

// Options are stored as [{label, value}] by imports, but builder-created
// questions also use {text, score}, bare strings, or a {options|choices} wrapper.
export const normalizeOptions = (item) => {
  if (TYPES_WITHOUT_OPTIONS.has(item?.type)) return [];
  let options = item?.options;
  if (options && !Array.isArray(options) && typeof options === 'object') {
    options = options.options || options.choices;
  }
  if (!Array.isArray(options)) return [];
  return options
    .map((opt, index) => {
      if (opt === null || opt === undefined) return null;
      if (typeof opt !== 'object') return { label: String(opt), code: String(index) };
      const label = opt.label ?? opt.text ?? opt.value ?? '';
      const code = opt.value ?? opt.score ?? opt.code ?? index;
      return { label: String(label), code: String(code) };
    })
    .filter(Boolean);
};

const numbersOrEmpty = (values) =>
  Array.isArray(values) ? values.filter((v) => v !== null && v !== undefined && v !== '') : [];

// IRT params come from imports ({slopes, thresholds}) or the Item Bank's IRT
// form ({discrimination, c1..cN}); keep file order rather than sorting.
const normalizeIrt = (irt) => {
  if (!irt || typeof irt !== 'object') return { slopes: [], thresholds: [] };
  let slopes = numbersOrEmpty(irt.slopes);
  if (slopes.length === 0) slopes = numbersOrEmpty([irt.discrimination ?? irt.slope]);
  let thresholds = numbersOrEmpty(irt.thresholds);
  if (thresholds.length === 0) {
    thresholds = numbersOrEmpty(
      Object.keys(irt)
        .filter((k) => /^c\d+$/.test(k))
        .sort((a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10))
        .map((k) => irt[k])
    );
  }
  return { slopes, thresholds };
};

const escapeCell = (value) => {
  const text = value === null || value === undefined || value === '' ? MISSING : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildAssessmentCsv = (formName, items = []) => {
  const rows = items.map((item, index) => ({
    item,
    // The importer skips rows without an ITEM_ID, so never leave it blank.
    itemId: item.code || item.cdisc_variable_name || `ITEM_${index + 1}`,
    options: normalizeOptions(item),
    irt: normalizeIrt(item.irt_parameters),
  }));

  const catCount = Math.max(1, ...rows.map((r) => r.options.length));
  const slopeCount = Math.max(1, ...rows.map((r) => r.irt.slopes.length));
  const interceptCount = Math.max(1, ...rows.map((r) => r.irt.thresholds.length));
  const range = (n) => Array.from({ length: n }, (_, i) => i + 1);

  const header = [
    'FORM_NAME', 'ITEM_ID', 'ITEM_TYPE', 'ITEM_TEXT', 'NUM_CAT',
    ...range(catCount).map((i) => `CAT_LABEL_${i}`),
    ...range(catCount).map((i) => `CAT_CODE_${i}`),
    'SCORE_TYPE', 'SCORE_NAME', 'SCORE_MODEL', 'NUM_RCODES', 'NUM_FAC', 'SCORE_FAC',
    ...range(slopeCount).map((i) => `A_${i}`),
    ...range(interceptCount).map((i) => `C_${i}`),
  ];

  const lines = rows.map(({ item, itemId, options, irt }) => {
    const params = item.irt_parameters || {};
    return [
      formName,
      itemId,
      toItemType(item.type),
      item.text,
      options.length,
      ...range(catCount).map((i) => options[i - 1]?.label),
      ...range(catCount).map((i) => options[i - 1]?.code),
      params.score_type,
      params.score_name,
      params.score_model,
      params.num_response_codes,
      params.num_factors,
      params.score_factor,
      ...range(slopeCount).map((i) => irt.slopes[i - 1]),
      ...range(interceptCount).map((i) => irt.thresholds[i - 1]),
    ].map(escapeCell).join(',');
  });

  return [header.join(','), ...lines].join('\n');
};

export const assessmentCsvFilename = (name) => {
  const base = (name || 'assessment').replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
  return `${base || 'assessment'}.csv`;
};

// No BOM: the importer decodes as plain UTF-8 first, which would keep the BOM
// glued to the FORM_NAME header and break format detection.
export const downloadCsv = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
